import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import css from './style.module.css';
import CopyButton from './CopyButton';
import { getOrgUsers, resetUserPassword, roleLabels } from './hqApi';

function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function ExistingUsers({ org }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [resetTarget, setResetTarget] = useState(null);
    const [credentials, setCredentials] = useState(null);
    const [resetError, setResetError] = useState(null);

    const { data: users = [], isLoading, isError } = useQuery({
        queryKey: ['hq-users', org?.orgId],
        queryFn: () => getOrgUsers(org.orgId),
        enabled: !!org?.orgId,
    });

    const resetMutation = useMutation({
        mutationFn: (uid) => resetUserPassword(uid),
        onSuccess: (data) => {
            setResetTarget(null);
            setCredentials(data?.credentials || null);
        },
        onError: (err) => {
            const status = err?.response?.status;
            if (status === 404) setResetError('User not found. Refresh the list and try again.');
            else if (status === 401) setResetError('You are not authorized to perform this action.');
            else setResetError(err?.response?.data?.message || 'Failed to reset the password.');
        },
    });

    const term = searchTerm.trim().toLowerCase();
    const filtered = term
        ? users.filter((user) =>
            [user.name, user.email, user.role].some(
                (value) => value && String(value).toLowerCase().includes(term)
            )
        )
        : users;

    const closeResetConfirm = () => {
        setResetTarget(null);
        setResetError(null);
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <span className="spinner-border" role="status" aria-hidden="true"></span>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="alert alert-danger" role="alert">
                Failed to load users for {org.companyName}. Please try again.
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <input
                    type="text"
                    className={css.searchInput}
                    placeholder="Search by name, email, or role…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className={css.maxRowsHint}>
                    {filtered.length} user{filtered.length === 1 ? '' : 's'}
                </span>
            </div>

            {filtered.length === 0 ? (
                <div className={css.emptyState}>
                    {users.length === 0
                        ? `No users exist in ${org.companyName} yet.`
                        : 'No matching users found.'}
                </div>
            ) : (
                <table className="rounded-table">
                    <thead className="table-header">
                        <tr>
                            <th className="py-1 px-2">Name</th>
                            <th className="py-1 px-2">Email</th>
                            <th className="py-1 px-2">Role</th>
                            <th className="py-1 px-2">Status</th>
                            <th className="py-1 px-2">Created</th>
                            <th className="py-1 px-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((user) => (
                            <tr key={user.id} className="table-row">
                                <td className="py-1 px-2">{user.name}</td>
                                <td className="py-1 px-2">{user.email}</td>
                                <td className="py-1 px-2">
                                    <span className={css.roleBadge}>{roleLabels[user.role] || user.role}</span>
                                </td>
                                <td className="py-1 px-2">
                                    <span className={user.active === 'Active' ? css.statusActive : css.statusInactive}>
                                        {user.active}
                                    </span>
                                </td>
                                <td className="py-1 px-2">{formatDate(user.createdAt)}</td>
                                <td className="py-1 px-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => {
                                            setResetError(null);
                                            setResetTarget(user);
                                        }}
                                    >
                                        <i className="fa-solid fa-key me-1" aria-hidden="true"></i>
                                        Reset password
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* Reset confirmation */}
            {resetTarget && (
                <div className={css.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="reset-title">
                    <motion.div
                        className={css.modalCard}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.2 }}
                    >
                        <button className={`btn ${css.modalClose}`} onClick={closeResetConfirm}>
                            ×
                        </button>
                        <div className="text-center">
                            <i className="fa-solid fa-key fa-2x text-warning mb-2" aria-hidden="true"></i>
                            <h5 id="reset-title">Reset password?</h5>
                            <p className="mb-0">
                                Generate a new temporary password for{' '}
                                <strong>{resetTarget.name}</strong> ({resetTarget.email})? Their
                                current password will stop working.
                            </p>
                        </div>
                        {resetError && (
                            <div className="alert alert-danger mt-3 mb-0" role="alert">
                                {resetError}
                            </div>
                        )}
                        <div className="d-flex justify-content-center mt-4 gap-3">
                            <button className="btn btn-secondary" onClick={closeResetConfirm} type="button">
                                Cancel
                            </button>
                            <button
                                className={`btn ${css.btnPrimary}`}
                                type="button"
                                disabled={resetMutation.isPending}
                                onClick={() => resetMutation.mutate(resetTarget.id)}
                            >
                                Reset password
                                {resetMutation.isPending && (
                                    <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                                )}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* New credentials */}
            {credentials && (
                <div className={css.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="cred-title">
                    <motion.div
                        className={css.modalCard}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.2 }}
                    >
                        <button className={`btn ${css.modalClose}`} onClick={() => setCredentials(null)}>
                            ×
                        </button>
                        <div className="text-center">
                            <h5 id="cred-title">New temporary password</h5>
                        </div>
                        <div className={css.warnBanner} role="alert">
                            <i className="fa-solid fa-triangle-exclamation mt-1" aria-hidden="true"></i>
                            <span>
                                This password is shown only once. Copy it now — you cannot retrieve
                                it later (only reset again).
                            </span>
                        </div>
                        <div className={css.credentialBlock}>
                            <div>Email: {credentials.email}</div>
                            <div>
                                Password: <span className={css.password}>{credentials.password}</span>
                            </div>
                        </div>
                        <p className={css.tempPasswordNote}>
                            The user will be asked to set a new password on first sign-in.
                        </p>
                        <div className="d-flex justify-content-center mt-3 gap-3">
                            <CopyButton
                                getText={() => `Email: ${credentials.email}\nPassword: ${credentials.password}`}
                                label="Copy credentials"
                                className={`btn btn-sm ${css.btnPrimary}`}
                            />
                            <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setCredentials(null)}>
                                Done
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
